import { useEffect, useState } from 'react'
import { PaginationMeta } from '../types/PaginationMeta'
import useApi from './useApi'
import usePagination from './usePagination'

type QueryValue = string | number | boolean | undefined

interface PaginatedResponse {
	meta: PaginationMeta
}

interface Options<TResponse, TRow> {
	path: string
	/** Filters, sort and scope. Compared by value; a change resets to page 1 and refetches once. */
	query?: Record<string, QueryValue>
	pageSize?: number
	getRows: (response: TResponse) => TRow[]
	enabled?: boolean
	/** Silent background refresh (no loading state); paused while the tab is hidden. */
	pollIntervalMs?: number
}

const NO_ROWS: never[] = []

/**
 * The single owner of list-page paging: builds the request, ignores stale responses, tracks
 * loading/error, and applies the response `meta` to the pagination state so callers cannot
 * forget to. Expects the backend list shape `{ ...rows, meta }` under `response.data`.
 */
function usePaginatedFetch<TResponse extends PaginatedResponse, TRow = unknown>({
	path,
	query = {},
	pageSize = 20,
	getRows,
	enabled = true,
	pollIntervalMs,
}: Options<TResponse, TRow>) {
	const api = useApi()
	const paging = usePagination(1, pageSize)
	const { current_page: currentPage, per_page: perPage } = paging.pagination

	const [response, setResponse] = useState<TResponse | null>(null)
	const [loading, setLoading] = useState(enabled)
	const [error, setError] = useState<unknown>(null)
	const [refreshToken, setRefreshToken] = useState(0)

	const queryKey = JSON.stringify(query)
	const [previousQueryKey, setPreviousQueryKey] = useState(queryKey)
	if (queryKey !== previousQueryKey) {
		// Adjusting state during render re-renders before effects run, so only one fetch fires.
		setPreviousQueryKey(queryKey)
		paging.onPageChange(1)
	}

	const fetchOnce = async (): Promise<TResponse> => {
		const params = new URLSearchParams()
		Object.entries(query).forEach(([key, value]) => {
			if (value !== undefined) {
				params.set(key, String(value))
			}
		})
		params.set('page', String(currentPage))
		params.set('perPage', String(perPage))
		const result = await api.get(`${path}?${params.toString()}`)
		return result.data as TResponse
	}

	useEffect(() => {
		if (!enabled) {
			setLoading(false)
			return
		}
		let cancelled = false
		setLoading(true)
		fetchOnce()
			.then((data) => {
				if (cancelled) {
					return
				}
				setResponse(data)
				paging.readMeta(data.meta)
				setError(null)
				setLoading(false)
			})
			.catch((err) => {
				if (cancelled) {
					return
				}
				setError(err)
				setLoading(false)
			})
		return () => {
			cancelled = true
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled, path, queryKey, currentPage, perPage, refreshToken])

	useEffect(() => {
		if (!enabled || !pollIntervalMs) {
			return
		}
		let cancelled = false
		const poll = async () => {
			if (document.visibilityState !== 'visible') {
				return
			}
			try {
				const data = await fetchOnce()
				if (!cancelled) {
					setResponse(data)
					paging.readMeta(data.meta)
				}
			} catch {
				// Best-effort refresh; a failed poll just waits for the next tick.
			}
		}
		const interval = setInterval(poll, pollIntervalMs)
		document.addEventListener('visibilitychange', poll)
		return () => {
			cancelled = true
			clearInterval(interval)
			document.removeEventListener('visibilitychange', poll)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled, pollIntervalMs, path, queryKey, currentPage, perPage])

	return {
		rows: response ? getRows(response) : (NO_ROWS as TRow[]),
		/** Raw response, for extra fields returned alongside the rows (e.g. summaries). */
		response,
		loading,
		error,
		pagination: paging.pagination,
		onPageChange: paging.onPageChange,
		refetch: () => setRefreshToken((token) => token + 1),
	}
}

export default usePaginatedFetch
